import {
  FC,
  createContext,
  PropsWithChildren,
  useContext,
  useMemo
} from 'react'

import usePopup, {
  PopupReturnType,
  PopupReturnTypeWithPayload
} from 'hooks/usePopup'
import { DeleteResponseUserPayload } from 'features/ContractDetails/ContractDetailsGeneral/ResponsibleUsers/DeleteUserPopup/types'
import { IRenewFinishDatePayload } from 'features/ContractDetails/ExtendContract/types'
import { ManualContractCreationRequestData } from 'features/ContractDetails/types'
import { IContractHealthSystem } from '../Facilities/types'
import { initialPopup } from '../../../hooks/constants'
import {
  acceptContractDocumentPopupWithPayload,
  addAmendmentPopupWithPayload,
  addSignatoryPopupWithPayload,
  assignResponsiblePopupWithPayload,
  deleteResponsibleUserPopupWithPayload,
  renewFinishDatePopupWithPayload,
  uploadAutoContractDocumentPopupWithPayload
} from './constants'
import { TUploadAutoContractDocumentsPayload } from '../ContractDetailsGeneral/ContractDetailsDocuments/UploadMultipleContractDocumentsPopup/types'

type ContextProps = {
  declineContractPopup: PopupReturnType
  terminateContractPopup: PopupReturnType
  deleteResponsibleUserPopup: PopupReturnTypeWithPayload<DeleteResponseUserPayload>
  extendContractPopup: PopupReturnType
  renewFinishDatePopup: PopupReturnTypeWithPayload<IRenewFinishDatePayload>
  contractStepsTimePopup: PopupReturnType
  addAmendmentPopup: PopupReturnTypeWithPayload<{
    selectedHSRow: IContractHealthSystem
  }>
  assignResponsiblePopup: PopupReturnTypeWithPayload<{
    creationDetails: Partial<ManualContractCreationRequestData>
  }>
  addResponsibleUserPopup: PopupReturnType
  sendForApprovalPopup: PopupReturnType
  changeTerminationDatePopup: PopupReturnType
  acceptContractDocumentPopup: PopupReturnTypeWithPayload<{
    documentId: string
  }>
  updateItemListPopup: PopupReturnType
  compareRecordsPopup: PopupReturnType
  reconcileDuplicatesPopup: PopupReturnType
  approveItemListPopup: PopupReturnType
  declineItemListPopup: PopupReturnType
  addSignatoryPopup: PopupReturnTypeWithPayload<{
    documentId: string
  }>
  changeTerminateDatePopup: PopupReturnType
  terminateContractWithDatePopup: PopupReturnType
  addNotePopup: PopupReturnType
  addContractCategoryPopup: PopupReturnType
  uploadAutoContractDocumentPopup: PopupReturnTypeWithPayload<TUploadAutoContractDocumentsPayload>
  editRebateTiersPopup: PopupReturnType
  editFacilityTiersPopup: PopupReturnType
}

const basePopup = (title: string) => ({
  title
})

const ContractDetailsPopupContext = createContext<ContextProps>({
  declineContractPopup: initialPopup,
  terminateContractPopup: initialPopup,
  deleteResponsibleUserPopup: deleteResponsibleUserPopupWithPayload,
  extendContractPopup: initialPopup,
  renewFinishDatePopup: renewFinishDatePopupWithPayload,
  contractStepsTimePopup: initialPopup,
  addAmendmentPopup: addAmendmentPopupWithPayload,
  assignResponsiblePopup: assignResponsiblePopupWithPayload,
  addResponsibleUserPopup: initialPopup,
  sendForApprovalPopup: initialPopup,
  acceptContractDocumentPopup: acceptContractDocumentPopupWithPayload,
  compareRecordsPopup: initialPopup,
  updateItemListPopup: initialPopup,
  reconcileDuplicatesPopup: initialPopup,
  approveItemListPopup: initialPopup,
  declineItemListPopup: initialPopup,
  addSignatoryPopup: addSignatoryPopupWithPayload,
  changeTerminationDatePopup: initialPopup,
  changeTerminateDatePopup: initialPopup,
  terminateContractWithDatePopup: initialPopup,
  addNotePopup: initialPopup,
  addContractCategoryPopup: initialPopup,
  uploadAutoContractDocumentPopup: uploadAutoContractDocumentPopupWithPayload,
  editRebateTiersPopup: initialPopup,
  editFacilityTiersPopup: initialPopup
})

const ContractDetailsPopupProvider: FC<PropsWithChildren> = ({ children }) => {
  const terminateContractPopupProps = usePopup(
    basePopup('Terminate contracting process?')
  )
  const terminateContractWithDatePopupProps = usePopup(
    basePopup('Terminate agreement?')
  )
  const changeTerminateDatePopupProps = usePopup(
    basePopup('Change Termination Date?')
  )
  const declineContractPopupProps = usePopup(basePopup('Decline'))
  const deleteResponsibleUserPopupProps = usePopup<DeleteResponseUserPayload>(
    basePopup('Delete team member?')
  )
  const extendContractPopupProps = usePopup(basePopup('Extend Agreement'))
  const renewFinishDatePopupProps = usePopup<IRenewFinishDatePayload>(
    basePopup('New finish dates are overlapped. Renew finish date anyway?')
  )
  const contractStepsTimePopupProps = usePopup(basePopup('Contract steps time'))
  const addAmendmentPopupProps = usePopup<{
    selectedHSRow: IContractHealthSystem
  }>(basePopup('participation details'))
  const addResponsibleUserPopupProps = usePopup(
    basePopup('Add team member to the contract')
  )
  const updateItemListPopupProps = usePopup(basePopup('Update price file'))
  const assignResponsiblePopupProps = usePopup<{
    creationDetails: Partial<ManualContractCreationRequestData>
  }>(basePopup('Assign responsible for the contract'))
  const sendForApprovalPopupProps = usePopup(basePopup('Send for approval?'))
  const acceptContractDocumentPopupProps = usePopup<{ documentId: string }>(
    basePopup('Do you want to accept the contract?')
  )
  const compareRecordsPopupProps = usePopup(basePopup('Compare records'))
  const declineItemListPopupProps = usePopup(basePopup('Decline'))
  const approveItemListPopupProps = usePopup(basePopup('Approve'))
  const reconcileDuplicatesPopupProps = usePopup(
    basePopup('Reconcile duplicate records')
  )
  const addSignatoryPopupProps = usePopup<{ documentId: string }>(
    basePopup('Add a Signatory')
  )
  const changeTerminationDatePopupProps = usePopup(
    basePopup('Change Termination Date')
  )
  const addContractCategoryPopupProps = usePopup(
    basePopup('Add contract category')
  )

  const addNotePopupProps = usePopup(basePopup('Contract note'))

  const uploadAutoContractDocumentPopupProps =
    usePopup<TUploadAutoContractDocumentsPayload>(basePopup('Add contract'))

  const editRebateTiersPopupProps = usePopup(
    basePopup('Edit rebate conditions')
  )

  const editFacilityTiersPopupProps = usePopup(basePopup('Edit facility tiers'))

  const context = useMemo(
    () => ({
      declineContractPopup: declineContractPopupProps,
      terminateContractPopup: terminateContractPopupProps,
      terminateContractWithDatePopup: terminateContractWithDatePopupProps,
      deleteResponsibleUserPopup: deleteResponsibleUserPopupProps,
      extendContractPopup: extendContractPopupProps,
      renewFinishDatePopup: renewFinishDatePopupProps,
      contractStepsTimePopup: contractStepsTimePopupProps,
      addAmendmentPopup: addAmendmentPopupProps,
      assignResponsiblePopup: assignResponsiblePopupProps,
      addResponsibleUserPopup: addResponsibleUserPopupProps,
      updateItemListPopup: updateItemListPopupProps,
      compareRecordsPopup: compareRecordsPopupProps,
      sendForApprovalPopup: sendForApprovalPopupProps,
      acceptContractDocumentPopup: acceptContractDocumentPopupProps,
      reconcileDuplicatesPopup: reconcileDuplicatesPopupProps,
      declineItemListPopup: declineItemListPopupProps,
      approveItemListPopup: approveItemListPopupProps,
      addSignatoryPopup: addSignatoryPopupProps,
      changeTerminationDatePopup: changeTerminationDatePopupProps,
      changeTerminateDatePopup: changeTerminateDatePopupProps,
      addNotePopup: addNotePopupProps,
      addContractCategoryPopup: addContractCategoryPopupProps,
      uploadAutoContractDocumentPopup: uploadAutoContractDocumentPopupProps,
      editRebateTiersPopup: editRebateTiersPopupProps,
      editFacilityTiersPopup: editFacilityTiersPopupProps
    }),
    [
      deleteResponsibleUserPopupProps,
      terminateContractPopupProps,
      terminateContractWithDatePopupProps,
      extendContractPopupProps,
      renewFinishDatePopupProps,
      contractStepsTimePopupProps,
      addAmendmentPopupProps,
      assignResponsiblePopupProps,
      declineContractPopupProps,
      addResponsibleUserPopupProps,
      sendForApprovalPopupProps,
      acceptContractDocumentPopupProps,
      compareRecordsPopupProps,
      updateItemListPopupProps,
      reconcileDuplicatesPopupProps,
      declineItemListPopupProps,
      approveItemListPopupProps,
      addSignatoryPopupProps,
      changeTerminationDatePopupProps,
      changeTerminateDatePopupProps,
      addNotePopupProps,
      addContractCategoryPopupProps,
      uploadAutoContractDocumentPopupProps,
      editRebateTiersPopupProps,
      editFacilityTiersPopupProps
    ]
  )

  return (
    <ContractDetailsPopupContext.Provider value={context}>
      {children}
    </ContractDetailsPopupContext.Provider>
  )
}

export const useContractDetailsPopup = () =>
  useContext(ContractDetailsPopupContext)

export default ContractDetailsPopupProvider
